import React, { useState } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import { CurriculumMeasurementResponse, StudentCurriculumMeasurementResponse, StudentCurriculumUnitMeasurementResponse } from '@dopingtech/live_expert_bff_api_ts';
import { useAuthStore } from '@/zustand/auth/store';
import { ProgressBar } from '@/components';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { Desktop, Mobile } from '@/utility/responsive';
import CustomPlaceholder from '@/components/custom-placeholder';

interface StudentCurriculumTopicMeasurementResponse {
	curriculumElementTopicId?: number;
	topicName?: string;
	measurement?: CurriculumMeasurementResponse;
}

const toRate = (measurement?: CurriculumMeasurementResponse) => Math.ceil((measurement?.performance?.successRateStatistics?.successRate || 0) * 100);

const renderSuccessBar = (rate: number, width?: number | string | false, backgroundColor: string = 'var(--surface-f9fdff)') => {
	const successClass = rate >= 70 ? 'high' : 'low';
	const color = successClass === 'high' ? '#1CA78C' : 'var(--text-d32f2f)';
	return (
		<div className="student-performance__lesson-item-header-stats-success">
			<div className="student-performance__lesson-item-header-stats-success-bar">
				<ProgressBar
					barColor={color}
					backgroundColor={backgroundColor}
					width={width || 80}
					height={10}
					value={rate}
				/>
			</div>
			<span className={clsx('student-performance__lesson-item-header-stats-success-value', `student-performance__lesson-item-header-stats-success-value--${successClass}`)}>%{rate}</span>
		</div>
	);
};

interface MeasurementRowProps {
	name?: string;
	measurement?: CurriculumMeasurementResponse;
	className?: string;
	prefix?: React.ReactNode;
	onClick?: () => void;
}

const MeasurementRow: React.FC<MeasurementRowProps> = ({ name, measurement, className, prefix, onClick }) => (
	<div
		className={clsx('student-performance__lesson-item-topics-item', className)}
		onClick={onClick}>
		<div className="student-performance__lesson-item-topics-item-col-lessons">
			{prefix}
			<div className="student-performance__lesson-item-topics-item-title">{name}</div>
		</div>
		<div className="student-performance__lesson-item-topics-item-col-curriculum">
			<div className="student-performance__lesson-item-topics-item-stats">
				<div className="student-performance__lesson-item-topics-item-stats-solved">
					{measurement?.completion?.progressInfo?.completedContent || 0}/{measurement?.completion?.materialSizeInfo?.totalContentCount || 0}
					<Mobile>
						<div className="student-performance__lesson-item-topics-item-stats-solved-description">Konu Anlatımı</div>
					</Mobile>
				</div>
			</div>
		</div>
		<div className="student-performance__lesson-item-topics-item-col-success-rate">
			<Mobile>
				<div className="student-performance__lesson-item-topics-item-col-success-rate-title">Soru Başarı Yüzdesi</div>
				<div className="student-performance__lesson-item-topics-item-stats-success">{renderSuccessBar(toRate(measurement), '100%', 'var(--surface-e1e5e7)')}</div>
			</Mobile>
			<Desktop>
				<div className="student-performance__lesson-item-topics-item-stats-success">{renderSuccessBar(toRate(measurement), false, 'var(--surface-e1e5e7)')}</div>
			</Desktop>
		</div>
	</div>
);

interface UnitItemProps {
	unit: StudentCurriculumUnitMeasurementResponse;
	curriculumId?: number;
	studentId: string;
}

const UnitItem: React.FC<UnitItemProps> = ({ unit, curriculumId, studentId }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { data: auth } = useAuthStore();

	const { data: topics, isLoading } = useQueryProvider(
		[QUERY_KEYS.getStudentCurriculumTopicsMeasurement, curriculumId?.toString() || '', unit.curriculumElementUnitId?.toString() || '', studentId],
		() =>
			axios.get<{ data?: StudentCurriculumTopicMeasurementResponse[] }>(`${process.env.LIVE_EXPERT_API_URL!.replace(/\/+$/, '')}/v1/measurement-evaluations/curriculum-topic-measurements`, {
				params: { studentId: parseInt(studentId), curriculumId, curriculumUnitId: unit.curriculumElementUnitId },
				headers: { Authorization: `Bearer ${auth?.access_token}` }
			}),
		{
			select: ({ data }) => data.data,
			enabled: isExpanded && !!studentId && !!curriculumId && !!unit.curriculumElementUnitId,
			refetchOnWindowFocus: false
		}
	);

	return (
		<div className={clsx('student-performance__lesson-item-unit', { 'student-performance__lesson-item-unit--expanded': isExpanded })}>
			<MeasurementRow
				name={unit.unitName}
				measurement={unit.measurement}
				className="student-performance__lesson-item-topics-item--clickable"
				onClick={() => setIsExpanded(prev => !prev)}
				prefix={<i className={clsx('student-performance__lesson-item-unit-toggle', isExpanded ? 'icons-alt-arrow-down' : 'icons-alt-arrow-right')} />}
			/>
			{isExpanded && (
				<div className="student-performance__lesson-item-unit-topics">
					{isLoading ? (
						<CustomPlaceholder
							ready={false}
							type="rect"
							height={40}
							className="student-performance__lesson-item-topics-placeholder">
							<div />
						</CustomPlaceholder>
					) : topics?.length ? (
						topics.map(topic => (
							<MeasurementRow
								key={topic.curriculumElementTopicId ?? topic.topicName}
								name={topic.topicName}
								measurement={topic.measurement}
								className="student-performance__lesson-item-topics-item--topic"
								prefix={<span className="student-performance__lesson-item-unit-dot" />}
							/>
						))
					) : (
						<div className="student-performance__lesson-item-unit-empty">Bu ünite için henüz konu kaydı bulunmuyor</div>
					)}
				</div>
			)}
		</div>
	);
};

interface LessonItemProps {
	lesson: StudentCurriculumMeasurementResponse;
	studentId: string;
}

const LessonItem: React.FC<LessonItemProps> = ({ lesson, studentId }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { MeasurementEvaluationControllerApi } = useApi();

	const { data: unitCurriculumMeasurements, isLoading: loadingUnitCurriculumMeasurements } = useQueryProvider(
		[QUERY_KEYS.getStudentCurriculumUnitsMeasurement, lesson?.curriculumId?.toString() || '', studentId?.toString()],
		() => MeasurementEvaluationControllerApi.getStudentCurriculumUnitsMeasurement(parseInt(studentId), lesson?.curriculumId || 0),
		{
			select: ({ data }) => data.data,
			enabled: !!studentId && isExpanded && !!lesson?.curriculumId,
			refetchOnWindowFocus: false
		}
	);

	const handleToggle = () => {
		setIsExpanded(prevState => !prevState);
	};

	const renderLessonIcon = () => {
		if (lesson.coverImageLink) {
			return <img src={lesson.coverImageLink} />;
		}

		return <i className="icons-document" />;
	};

	return (
		<div className="student-performance__lesson-item">
			<div
				className="student-performance__lesson-item-header"
				onClick={handleToggle}>
				<Desktop>
					<div className="student-performance__lesson-item-col-lessons">
						<div className="student-performance__lesson-item-header-icon">{renderLessonIcon()}</div>
						<div className="student-performance__lesson-item-header-title">{lesson.curriculumName}</div>
					</div>
					<div className="student-performance__lesson-item-col-curriculum">
						<div className="student-performance__lesson-item-header-stats">
							<div className="student-performance__lesson-item-header-stats-solved">
								{Math.ceil(lesson.measurement?.completion?.progressInfo?.completedContent || 0)}/{lesson.measurement?.completion?.materialSizeInfo?.totalContentCount || 0}
							</div>
						</div>
					</div>
					<div className="student-performance__lesson-item-col-success-rate">
						{renderSuccessBar(Math.ceil((lesson.measurement?.performance?.successRateStatistics?.successRate || 0) * 100), 80)}
					</div>
					<div
						className={clsx('student-performance__lesson-item-header-toggle', {
							'student-performance__lesson-item-header-toggle--expanded': isExpanded
						})}>
						<i className="icons-alt-arrow-down" />
					</div>
				</Desktop>
				<Mobile>
					<div className="student-performance__lesson-item-mobile">
						<div className="student-performance__lesson-item-mobile-top">
							<div className="student-performance__lesson-item-mobile-icon">{renderLessonIcon()}</div>
							<div className="student-performance__lesson-item-mobile-title">{lesson.curriculumName}</div>
							<div
								className={clsx('student-performance__lesson-item-mobile-toggle', {
									'student-performance__lesson-item-mobile-toggle--expanded': isExpanded
								})}>
								<i className="icons-alt-arrow-down" />
							</div>
						</div>

						<div className="student-performance__lesson-item-mobile-stats">
							<div className="student-performance__lesson-item-mobile-stat">
								<div className="student-performance__lesson-item-mobile-stat-label">Konu Anlatımı</div>
								<div className="student-performance__lesson-item-mobile-stat-value">
									{Math.ceil(lesson.measurement?.completion?.progressInfo?.completedContent || 0)}/{lesson.measurement?.completion?.materialSizeInfo?.totalContentCount || 0}
								</div>
							</div>
							<div className="student-performance__lesson-item-mobile-stats-divider" />

							<div className="student-performance__lesson-item-mobile-stat">
								<div className="student-performance__lesson-item-mobile-stat-label">Soru Çözme</div>
								<div className="student-performance__lesson-item-mobile-stat-progress">
									{renderSuccessBar(Math.ceil((lesson.measurement?.performance?.successRateStatistics?.successRate || 0) * 100))}
								</div>
							</div>
						</div>
					</div>
				</Mobile>
			</div>
			{isExpanded && (
				<div className="student-performance__lesson-item-topics">
					{loadingUnitCurriculumMeasurements ? (
						Array.from({ length: 3 }, (_, index) => (
							<CustomPlaceholder
								key={`topic-placeholder-${index}`}
								ready={false}
								type="rect"
								height={60}
								className="student-performance__lesson-item-topics-placeholder">
								<div />
							</CustomPlaceholder>
						))
					) : unitCurriculumMeasurements?.length ? (
						unitCurriculumMeasurements.map(unit => (
							<UnitItem
								key={unit.curriculumElementUnitId}
								unit={unit}
								curriculumId={lesson?.curriculumId}
								studentId={studentId}
							/>
						))
					) : (
						<div className="student-performance__lesson-item-topics-empty">
							<div className="student-performance__lesson-item-topics-empty-icon">
								<i className="icons-document" />
							</div>
							<div className="student-performance__lesson-item-topics-empty-text">Bu ders için henüz konu kaydı bulunmuyor</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default LessonItem;
